import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiDownload,
  FiEye,
  FiArrowLeft,
  FiClock,
  FiFile,
  FiLoader,
  FiBook,
} from 'react-icons/fi';
import { Card, LoaderComponent, EmptyState } from '../components/ui';
import api from '../services/api';
import { fmtInstante } from '../utils/tempoCv';

interface DigitalAccess {
  id: string;
  produtoNome: string;
  produtoDescricao?: string;
  lojaNome: string;
  formato?: string;
  tamanhoMb?: number;
  nomeFicheiro?: string;
  downloadsUsados: number;
  maxDownloads?: number;
  dataExpiracao?: string;
  dataCompra: string;
}

const formatDate = (dateStr?: string) => {
  if (!dateStr) return null;
  return fmtInstante(dateStr, {
    year: 'numeric', month: 'long', day: 'numeric',
  });
};

const MeusDownloads: React.FC = () => {
  const navigate = useNavigate();
  const [accesses, setAccesses] = useState<DigitalAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Track which access is currently being opened
  const [openingId, setOpeningId] = useState<string | null>(null);

  useEffect(() => {
    sessionStorage.setItem('pageTitle', 'Meus Downloads');
    // Ligar firebase_uid a compras anteriores (aguardar antes de carregar)
    api.post('/client/digital/link-uid')
      .catch(() => {})
      .finally(() => loadAccesses());
  }, []);

  const loadAccesses = async () => {
    setLoading(true);
    try {
      const res = await api.get('/client/digital/my-accesses');
      if (res.data?.success) {
        setAccesses(res.data.data || []);
      } else {
        setError(res.data?.error || 'Erro ao carregar compras digitais');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Erro ao carregar compras digitais');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Abre o produto digital via endpoint autenticado.
   * O servidor verifica que o user logado é o dono da compra antes de gerar o link.
   */
  const handleOpen = async (accessId: string, mode: 'download' | 'view') => {
    setOpeningId(`${accessId}-${mode}`);
    try {
      const res = await api.get(`/client/digital/${mode}/${accessId}`);
      if (res.data?.success && res.data?.data?.url) {
        window.open(res.data.data.url, '_blank');
      } else {
        alert('Não foi possível gerar o link. Tenta novamente.');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || err.message || 'Erro ao aceder ao produto';
      alert(msg);
    } finally {
      setOpeningId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <LoaderComponent size="md" text="A carregar os teus downloads..." />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-full hover:bg-pagali-gray-100 transition-colors"
        >
          <FiArrowLeft className="text-xl text-pagali-gray-700" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-pagali-gray-900">Meus Downloads</h1>
          <p className="text-sm text-pagali-gray-500">Os teus produtos digitais adquiridos</p>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          {error}
        </div>
      )}

      {!error && accesses.length === 0 ? (
        <EmptyState
          icon={<FiDownload className="text-5xl" />}
          title="Nenhuma compra digital encontrada"
          description="Quando comprares produtos digitais, aparecem aqui para download."
          action={
            <button onClick={() => navigate('/ecommerce')} className="btn-primary mt-4">
              Explorar lojas
            </button>
          }
        />
      ) : (
        <div className="space-y-4">
          {accesses.map((access) => (
            <Card key={access.id} className="p-4">
              <div className="flex flex-col gap-3">
                {/* Product info */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-pagali-gray-900 truncate">
                      {access.produtoNome}
                    </h3>
                    <p className="text-xs text-pagali-gray-500 mt-0.5">{access.lojaNome}</p>
                  </div>
                  {access.formato && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full whitespace-nowrap">
                      <FiFile className="text-xs" />
                      {access.formato.toUpperCase()}
                    </span>
                  )}
                </div>

                {/* Meta info */}
                <div className="flex flex-wrap gap-3 text-xs text-pagali-gray-500">
                  {access.tamanhoMb && <span>{access.tamanhoMb} MB</span>}
                  {access.maxDownloads ? (
                    <span>{access.downloadsUsados}/{access.maxDownloads} downloads usados</span>
                  ) : (
                    <span>{access.downloadsUsados} download(s) realizados</span>
                  )}
                  {access.dataCompra && (
                    <span>Comprado em {formatDate(access.dataCompra)}</span>
                  )}
                </div>

                {/* Expiration */}
                {access.dataExpiracao && (
                  <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                    <FiClock className="flex-shrink-0" />
                    <span>Expira em {formatDate(access.dataExpiracao)}</span>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-1 flex-wrap">
                  {/* Ler — leitor embutido (PDF e formatos de texto) */}
                  {(!access.formato || ['pdf', 'epub', 'mobi'].includes(access.formato.toLowerCase())) && (
                    <button
                      onClick={() => navigate(`/reader/${access.id}`)}
                      className="flex-1 min-w-[100px] flex items-center justify-center gap-2 py-2.5 bg-pagali-purple text-white text-sm font-medium rounded-xl hover:bg-pagali-purple-light transition-colors"
                    >
                      <FiBook />
                      Ler
                    </button>
                  )}
                  {/* Download */}
                  <button
                    onClick={() => handleOpen(access.id, 'download')}
                    disabled={!!openingId}
                    className="flex-1 min-w-[100px] flex items-center justify-center gap-2 py-2.5 border border-pagali-gray-200 text-pagali-gray-700 text-sm font-medium rounded-xl hover:bg-pagali-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {openingId === `${access.id}-download`
                      ? <FiLoader className="animate-spin" />
                      : <FiDownload />}
                    Guardar
                  </button>
                  {/* Visualizar noutros formatos (zip, mp4, etc.) */}
                  {access.formato && !['pdf', 'epub', 'mobi'].includes(access.formato.toLowerCase()) && (
                    <button
                      onClick={() => handleOpen(access.id, 'view')}
                      disabled={!!openingId}
                      className="flex-1 min-w-[100px] flex items-center justify-center gap-2 py-2.5 border border-pagali-gray-200 text-pagali-gray-700 text-sm font-medium rounded-xl hover:bg-pagali-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {openingId === `${access.id}-view`
                        ? <FiLoader className="animate-spin" />
                        : <FiEye />}
                      Abrir
                    </button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MeusDownloads;
